// ============================================================================
// finAuditoriaFormat — regras PURAS das telas da Auditoria (Financeiro,
// pacote 1, 07/10/2026)
// ============================================================================
// Pedido do dono: "nosso sistema pode acusar quando tiver esses erros…
// precisamos auditar para saber se não estamos sendo roubados". O servidor
// (/api/fin e /api/fin-extrato) entrega tudo pronto: alertas, agenda de
// recebíveis, conferência com o extrato e taxas. Aqui só se FORMATA e se
// ARRUMA para a tela — nenhum dinheiro é recalculado (exceto diferenças de
// exibição, sempre em centavos inteiros).
//   · severidade, status, tipo do alerta (rótulos e cores da paleta DNA azul);
//   · antes × depois dos pagamentos de uma comanda (linhas lado a lado);
//   · filtros → query do servidor (sem campos vazios);
//   · recebimentos por dia/unidade, PIX e mês (tolerante ao formato);
//   · matriz das taxas Stone; prefill do "Lançar em Contas a Pagar";
//   · comanda apagada: a FORMA de pagamento da última foto e a pista de PIX do
//     extrato (pedido do dono, 07/10/2026: "saber se foi dinheiro ou PIX").
// Nada aqui tem nome de pessoa: os nomes só aparecem no detalhe do alerta,
// vindos do servidor, e só para o operador financeiro.
// Testes: tests/fin-auditoria-telas.test.mjs.
// ============================================================================

import { fmtCentavos, fmtPct, dataBR, dataHoraBR } from './dreFormat.js';
import { diaSP } from './dataSP.js';
import { formaDaClasse, descricaoDoExtrato, contaBancariaDaConta, centroCustoDaCategoria, mesAno } from './finTituloCampos.js';
import { R as ROTULOS, RU } from './finRotulos.js';

export { fmtCentavos, fmtPct, dataBR, dataHoraBR };

export const MIN_MOTIVO = 10;
export const ORDEM_UNIDADES = ['unit-moema', 'unit-vila', 'unit-oscar', 'unit-jardins', 'unit-itaim'];
const NOME_PADRAO_UNIDADE = {
  'unit-moema': 'Moema',
  'unit-vila': 'Vila Olímpia',
  'unit-oscar': 'Oscar Freire',
  'unit-jardins': 'Jardins',
  'unit-itaim': 'Itaim Bibi',
};

// ─── Unidades ───────────────────────────────────────────────────────────────
/** Map id → nome (as unidades do sistema; reserva = nomes fixos das 5). */
export function mapaUnidades(unidades = []) {
  const m = new Map(Object.entries(NOME_PADRAO_UNIDADE));
  for (const u of unidades || []) if (u?.id) m.set(u.id, u.name || u.nome || m.get(u.id) || u.id);
  return m;
}
export function nomeUnidade(unitId, mapa) {
  if (!unitId) return 'Rede';
  return (mapa && mapa.get(unitId)) || NOME_PADRAO_UNIDADE[unitId] || unitId;
}
/** As 5 unidades do Avec na ordem da DRE (só as que existem no mapa). */
export function unidadesAuditadas(mapa) {
  return ORDEM_UNIDADES.map(id => ({ id, nome: nomeUnidade(id, mapa) }));
}
const ordemUnidade = (id) => { const i = ORDEM_UNIDADES.indexOf(id); return i < 0 ? 99 : i; };

// ─── Severidade e status ───────────────────────────────────────────────────
// alta = vermelho (danger), média = âmbar (warning), baixa = slate, info = brand-50
export const SEVERIDADES = ['alta', 'media', 'baixa', 'info'];
export const SEVERIDADE = {
  alta: { rotulo: 'Grave', plural: 'graves', classe: 'bg-danger-soft text-danger-text border-danger/30', ponto: 'bg-danger', ordem: 4 },
  media: { rotulo: 'Média', plural: 'médias', classe: 'bg-warning-soft text-warning-text border-warning/30', ponto: 'bg-warning', ordem: 3 },
  baixa: { rotulo: 'Baixa', plural: 'baixas', classe: 'bg-slate-100 text-slate-600 border-slate-200', ponto: 'bg-slate-400', ordem: 2 },
  info: { rotulo: 'Info', plural: 'informativos', classe: 'bg-brand-50 text-brand-700 border-brand-100', ponto: 'bg-brand-300', ordem: 1 },
};
export const severidadeDe = (s) => SEVERIDADE[s] || { rotulo: s || '—', plural: s || '—', classe: 'bg-slate-100 text-slate-600 border-slate-200', ponto: 'bg-slate-300', ordem: 0 };

export const STATUS = {
  aberto: { rotulo: 'Para conferir', classe: 'bg-white text-ink border-slate-300' },
  resolvido: { rotulo: 'Resolvido', classe: 'bg-success-soft text-success-text border-success/20' },
  justificado: { rotulo: 'Justificado', classe: 'bg-brand-50 text-brand-700 border-brand-100' },
  ignorado: { rotulo: 'Ignorado', classe: 'bg-slate-100 text-slate-500 border-slate-200' },
};
export const statusDe = (s) => STATUS[s] || { rotulo: s || '—', classe: 'bg-slate-100 text-slate-600 border-slate-200' };

/** Filtro de status da lista (o servidor aceita aberto | fechados | todos | um status). */
export const FILTRO_STATUS = [
  { id: 'aberto', rotulo: 'Para conferir' },
  { id: 'fechados', rotulo: 'Resolvidos' },
  { id: 'todos', rotulo: 'Todos' },
];

/** Ações da pessoa sobre um alerta: status de destino + textos. */
export const ACOES_ALERTA = {
  resolvido: { rotulo: 'Resolver', titulo: 'Resolver o alerta', dica: 'O que foi feito para corrigir (ex.: comanda refeita com o pagamento certo; título lançado).' },
  justificado: { rotulo: 'Justificar', titulo: 'Justificar o alerta', dica: 'Por que está certo assim (ex.: cliente pagou parte em dinheiro, conferido com a gerente).' },
  ignorado: { rotulo: 'Ignorar', titulo: 'Ignorar o alerta', dica: 'Por que não precisa de ação (ex.: teste da recepção, comanda de treinamento).' },
  aberto: { rotulo: 'Reabrir', titulo: 'Reabrir o alerta', dica: 'Por que voltar a conferir (ex.: a justificativa não se confirmou).' },
};
/** Que ações cabem no status atual. */
export function acoesPossiveis(status) {
  return status === 'aberto' ? ['resolvido', 'justificado', 'ignorado'] : ['aberto'];
}

export const motivoValido = (m) => String(m || '').trim().length >= MIN_MOTIVO;
/** Quantas letras ainda faltam para o motivo valer (0 = ok). */
export const letrasFaltando = (m) => Math.max(0, MIN_MOTIVO - String(m || '').trim().length);

// ─── Catálogo de tipos (vem do servidor: objeto por tipo OU lista) ──────────
export function listaCatalogo(catalogo) {
  if (!catalogo) return [];
  if (Array.isArray(catalogo)) return catalogo.filter(i => i && i.tipo).map(i => ({ ...i }));
  return Object.entries(catalogo).filter(([, v]) => v && typeof v === 'object').map(([tipo, v]) => ({ tipo, ...v }));
}
export function itemCatalogo(tipo, catalogo) {
  if (!tipo || !catalogo) return null;
  if (Array.isArray(catalogo)) return catalogo.find(i => i?.tipo === tipo) || null;
  return catalogo[tipo] ? { tipo, ...catalogo[tipo] } : null;
}
const humanizar = (s) => {
  const t = String(s || '').replace(/_/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '—';
};
/** Nome do tipo para a tela (catálogo do servidor; reserva = o código legível). */
export function rotuloTipo(tipo, catalogo) {
  const it = itemCatalogo(tipo, catalogo);
  return (it && (it.titulo || it.nome)) || humanizar(tipo);
}

// ─── Formas de pagamento ───────────────────────────────────────────────────
export const FORMA_TXT = {
  credito: 'Crédito', debito: 'Débito', pix: 'PIX', dinheiro: 'Dinheiro', transferencia: 'Transferência',
  cortesia: 'Cortesia', permuta: 'Permuta', voucher: 'Voucher', credito_cliente: 'Crédito do cliente', outra: 'Outra forma',
};
export const BANDEIRA_TXT = { visa_master: 'Visa/Master', elo: 'Elo', amex: 'Amex', outras: 'Outra bandeira' };
// Grupos de dinheiro das regras (cartão = crédito + débito; não venda = cortesia/permuta/voucher)
const GRUPO_PARA = {
  cartao: 'cartão', pix: 'PIX', dinheiro: 'dinheiro', nao_venda: 'cortesia/permuta/voucher',
  credito_cliente: 'crédito do cliente', transferencia: 'transferência', outra: 'outra forma',
};
const GRUPO_DE = {
  cartao: 'do cartão', pix: 'do PIX', dinheiro: 'do dinheiro', nao_venda: 'de cortesia/permuta/voucher',
  credito_cliente: 'do crédito do cliente', transferencia: 'da transferência', outra: 'de outra forma',
};
export const textoGrupo = (g) => GRUPO_PARA[g] || humanizar(g).toLowerCase();

/** { de:'cartao', para:'dinheiro', valor_cents:14000 } → "R$ 140,00 saiu do cartão e foi para dinheiro" */
export function textoMovimento({ de, para, valor_cents } = {}) {
  const deTxt = GRUPO_DE[de] || `de ${textoGrupo(de)}`;
  return `${fmtCentavos(valor_cents)} saiu ${deTxt} e foi para ${textoGrupo(para)}`;
}

/** Um pagamento numa linha: "Crédito 3x · Visa/Master" */
export function textoPagamento(p) {
  if (!p) return '—';
  const forma = FORMA_TXT[p.forma_grupo] || p.forma || 'Pagamento';
  const parc = Number(p.parcelas) > 1 ? ` ${Number(p.parcelas)}x` : '';
  const band = p.bandeira_grupo ? ` · ${BANDEIRA_TXT[p.bandeira_grupo] || p.bandeira_grupo}` : '';
  return `${forma}${parc}${band}`;
}

export const chavePagamento = (p) =>
  [p?.forma_grupo || '', Number(p?.valor_cents) || 0, Number(p?.parcelas) || 1, p?.bandeira_grupo || ''].join('|');

export const somaPagamentos = (lista) => (lista || []).reduce((s, p) => s + (Number(p?.valor_cents) || 0), 0);

/**
 * Antes × depois dos pagamentos de uma comanda, em linhas lado a lado.
 * Pareia primeiro os iguais; depois mesma forma e valor (mudou parcela ou
 * bandeira); depois mesmo valor (mudou a forma); depois mesma forma (mudou o
 * valor). O que sobra: "saiu" (só antes) ou "entrou" (só depois).
 * → [{ antes, depois, estado: 'igual'|'mudou'|'saiu'|'entrou', mudancas: ['forma'|'valor'|'parcelas'|'bandeira'] }]
 */
export function diffPagamentos(antes = [], depois = []) {
  const A = (antes || []).map((p, i) => ({ p, i, usado: false }));
  const D = (depois || []).map((p, i) => ({ p, i, usado: false }));
  const linhas = [];
  const mesmoValor = (a, d) => Number(a?.valor_cents) === Number(d?.valor_cents);
  const parear = (cond, estado) => {
    for (const a of A) {
      if (a.usado) continue;
      const d = D.find(x => !x.usado && cond(a.p, x.p));
      if (d) {
        a.usado = true;
        d.usado = true;
        linhas.push({ antes: a.p, depois: d.p, estado, _ordem: a.i });
      }
    }
  };
  parear((a, d) => chavePagamento(a) === chavePagamento(d), 'igual');
  parear((a, d) => a?.forma_grupo === d?.forma_grupo && mesmoValor(a, d), 'mudou');
  parear((a, d) => mesmoValor(a, d), 'mudou');
  parear((a, d) => a?.forma_grupo === d?.forma_grupo, 'mudou');
  for (const a of A) if (!a.usado) linhas.push({ antes: a.p, depois: null, estado: 'saiu', _ordem: a.i });
  linhas.sort((x, y) => x._ordem - y._ordem);
  D.filter(d => !d.usado).forEach(d => linhas.push({ antes: null, depois: d.p, estado: 'entrou', _ordem: 1e6 + d.i }));
  return linhas.map(({ _ordem, ...l }) => {
    const mudancas = [];
    if (l.estado === 'mudou') {
      if (l.antes.forma_grupo !== l.depois.forma_grupo) mudancas.push('forma');
      if (!mesmoValor(l.antes, l.depois)) mudancas.push('valor');
      if ((Number(l.antes.parcelas) || 1) !== (Number(l.depois.parcelas) || 1)) mudancas.push('parcelas');
      if ((l.antes.bandeira_grupo || '') !== (l.depois.bandeira_grupo || '')) mudancas.push('bandeira');
    }
    return { ...l, mudancas };
  });
}

export const MOTIVO_MUDANCA_TXT = {
  forma_sensivel: 'saiu do cartão/PIX para forma sensível',
  total_pago_menor: 'o total pago diminuiu',
  total_comanda_menor: 'o total da comanda diminuiu',
  parcelas: 'mudaram as parcelas',
  bandeira: 'mudou a bandeira',
  data_trocada: 'a data da comanda mudou',
  reaberta: 'a comanda foi reaberta e finalizada de novo',
  outro: 'outra mudança nos pagamentos',
  sem_confirmacao_0090: 'sem confirmação do relatório de comandas apagadas',
};
export const textoMotivoMudanca = (m) => MOTIVO_MUDANCA_TXT[m] || humanizar(m).toLowerCase();

export const EVENTO_VERSAO_TXT = { nova: 'primeira foto', alterada: 'alterada', sumiu: 'pagamentos sumiram' };
export const EVENTO_ALERTA_TXT = {
  criado: 'Alerta criado',
  status: 'Status mudado',
  auto_resolvido: 'Resolvido sozinho (a condição sumiu na coleta)',
  reaberto: 'Reaberto',
  notificado: 'Aviso enviado ao celular',
  resumo: 'Entrou no resumo das 9h',
};

// ─── Filtros da lista → query do servidor ──────────────────────────────────
/**
 * Estado dos filtros da tela → parâmetros do GET op=alertas, SEM campos vazios
 * (o servidor recusa "unit_id="). Listas viram "a,b". O status vai sempre
 * explícito; "Mostrar histórico" pede 'todos' (o padrão do servidor é só os
 * fatos depois do início da vigilância).
 */
export function filtrosParaQuery(f = {}) {
  const q = {};
  const put = (k, v) => {
    if (v === undefined || v === null) return;
    if (Array.isArray(v)) {
      const l = v.map(x => String(x ?? '').trim()).filter(Boolean);
      if (l.length) q[k] = l.join(',');
      return;
    }
    const s = String(v).trim();
    if (s) q[k] = s;
  };
  put('status', f.status || 'aberto');
  if (f.mostrarHistorico === true) q.historico = 'todos';
  else put('historico', f.historico);
  put('unit_id', f.unit_id);
  put('tipo', f.tipos ?? f.tipo);
  put('severidade', f.severidades ?? f.severidade);
  put('de', f.de);
  put('ate', f.ate);
  if (f.limite !== undefined && f.limite !== null && f.limite !== '') put('limite', Math.max(1, Math.min(500, Number(f.limite) || 100)));
  put('antes_de_id', f.antes_de_id);
  return q;
}

// ─── Datas ──────────────────────────────────────────────────────────────────
const RE_DIA = /^(\d{4})-(\d{2})-(\d{2})/;
/** 'AAAA-MM-DD' ± n dias corridos. */
export function somarDias(dia, n) {
  const m = String(dia || '').match(RE_DIA);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + Number(n || 0))).toISOString().slice(0, 10);
}
/** Dias de a até b (b − a). */
export function diasEntre(a, b) {
  const ma = String(a || '').match(RE_DIA);
  const mb = String(b || '').match(RE_DIA);
  if (!ma || !mb) return null;
  return Math.round((Date.UTC(+mb[1], +mb[2] - 1, +mb[3]) - Date.UTC(+ma[1], +ma[2] - 1, +ma[3])) / 86400000);
}
const SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
export function diaSemanaCurto(dia) {
  const m = String(dia || '').match(RE_DIA);
  if (!m) return '';
  return SEMANA[new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay()];
}
/** 'AAAA-MM-DD' → 'DD/MM' */
export function diaMes(dia) {
  const m = String(dia || '').match(RE_DIA);
  return m ? `${m[3]}/${m[2]}` : '—';
}
/** 'AAAA-MM' → 'MM/AAAA' */
export function competenciaBR(c) {
  const m = String(c || '').match(/^(\d{4})-(\d{2})$/);
  return m ? `${m[2]}/${m[1]}` : String(c || '—');
}
/** Hora 'HH:MM' de um instante, no fuso de São Paulo. */
export function horaSP(iso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '—';
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(d);
  const g = (t) => p.find(x => x.type === t)?.value || '00';
  return `${g('hour')}:${g('minute')}`;
}
/** "hoje 14:30" | "ontem 22:30" | "05/10 06:31" (fuso SP). */
export function quandoCurto(iso, agora = new Date()) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '—';
  const dia = diaSP(d);
  const hoje = diaSP(agora instanceof Date ? agora : new Date(agora));
  const h = horaSP(iso);
  if (dia === hoje) return `hoje ${h}`;
  if (dia === somarDias(hoje, -1)) return `ontem ${h}`;
  return `${diaMes(dia)} ${h}`;
}

// ─── Cabeçalho da Auditoria ────────────────────────────────────────────────
const MODO_COLETA_TXT = { diaria: 'completa', hoje: 'foto de hoje', mensal: 'mensal', manual: 'pedida à mão' };
/**
 * ultima_coleta do op=alertas-contagem → { texto, tom: 'ok'|'aviso'|'erro' }.
 * Mais de 26 h sem coleta = aviso (a completa roda todo dia às 6h30).
 */
export function ultimaColetaTexto(ultima, { agora = new Date(), mapa = null } = {}) {
  if (!ultima || !ultima.quando) return { texto: 'ainda não rodou', tom: 'aviso' };
  const quando = quandoCurto(ultima.quando, agora);
  const modo = MODO_COLETA_TXT[ultima.modo] ? `, ${MODO_COLETA_TXT[ultima.modo]}` : '';
  const comErro = Array.isArray(ultima.unidades_com_erro) ? ultima.unidades_com_erro.filter(Boolean) : [];
  const horas = (new Date(agora).getTime() - new Date(ultima.quando).getTime()) / 3600000;
  if (comErro.length || ultima.ok === false) {
    const nomes = comErro.map(u => nomeUnidade(u, mapa)).join(', ');
    return { texto: `${quando}${modo} (com falha${nomes ? ` em ${nomes}` : ''})`, tom: 'erro' };
  }
  if (horas > 26) return { texto: `${quando}${modo} (atrasada: mais de 1 dia sem coleta)`, tom: 'aviso' };
  return { texto: `${quando}${modo} (ok)`, tom: 'ok' };
}

/**
 * saude do op=alertas-contagem → texto do aviso vermelho, ou null se está tudo
 * rodando. Atrasada = a rodada automática completa (a cada 30 min; a foto de hoje
 * roda a cada 10) não roda há mais de
 * 75 min. Problemas = o que o servidor viu (avaliação falhando, diária atrasada,
 * comandas suspeitas). Sem a saúde (servidor antigo/primeira rodada) → null.
 */
export function textoSaude(saude, { agora = new Date(), mapa = null } = {}) {
  if (!saude || typeof saude !== 'object') return null;
  const partes = [];
  if (saude.atrasada) partes.push(`A rodada automática não roda desde ${quandoCurto(saude.em, agora)}.`);
  const probs = Array.isArray(saude.problemas) ? saude.problemas : [];
  if (probs.length) {
    const txt = probs.slice(0, 3).map(p => `${p.unit_id ? `${nomeUnidade(p.unit_id, mapa)}: ` : ''}${p.texto || p.motivo || 'problema'}`).join('; ');
    partes.push(`${txt}${probs.length > 3 ? ` (e mais ${probs.length - 3})` : ''}.`);
  } else if (saude.ok === false) {
    partes.push('A última rodada encontrou um problema.');
  }
  return partes.length ? partes.join(' ') : null;
}

/** vigilancia {unit_id: dia|null} → "desde 01/10/2026" (+ unidades ainda sem). */
export function textoVigilancia(vigilancia, mapa = null) {
  const ent = Object.entries(vigilancia || {});
  const dias = ent.map(([, d]) => d).filter(Boolean).sort();
  if (!dias.length) return { texto: 'ainda não começou', semVigilancia: ent.map(([u]) => nomeUnidade(u, mapa)) };
  const sem = ent.filter(([, d]) => !d).map(([u]) => nomeUnidade(u, mapa));
  return { texto: `desde ${dataBR(dias[0])}`, semVigilancia: sem };
}

/** Número do contador ("" some; > 99 vira "99+"). */
export function textoBadge(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '';
  return v > 99 ? '99+' : String(Math.floor(v));
}

/** Contagem do servidor → números seguros para a tela. */
export function resumoContagem(contagem) {
  const a = contagem?.abertos || {};
  const n = (x) => (Number.isFinite(Number(x)) ? Number(x) : 0);
  return {
    alta: n(a.alta), media: n(a.media), baixa: n(a.baixa), info: n(a.info),
    badge: n(contagem?.badge ?? (n(a.alta) + n(a.media))),
    historico: n(contagem?.historico_abertos),
  };
}

// ─── Campos genéricos do alerta (dados do servidor, sem PII) ───────────────
const ROTULO_CAMPO = {
  data: 'Data da comanda', numero: 'Comanda nº', apagada_em: 'Apagada em', dias_depois: 'Dias depois da data',
  tinha_pagamento_cents: 'Tinha pagamento na foto', formas_na_foto: 'Formas na foto', excluido_em: 'Excluído em',
  finalizada_em: 'Finalizada em', minutos_apos: 'Minutos depois de finalizar', minutos_apos_finalizar: 'Minutos depois de finalizar',
  registrado_em: 'Registrado em', forma: 'Forma', valor_cents: 'Valor', pagamentos_cents: 'Pagamentos',
  total_cents: 'Total', excesso_cents: 'Excesso', quantidade: 'Quantidade', numeros: 'Comandas',
  data_lancada: 'Data lançada', dia_real: 'Dia real', motivo: 'Motivo', escopo: 'Escopo', formas: 'Formas',
  limite_cents: 'Limite', abertura: 'Abertura', fechamento: 'Fechamento', horas_aberto: 'Horas aberto',
  competencia: 'Competência', caixas: 'Caixas', caixas_sem_contagem: 'Caixas sem contagem', dia: 'Dia',
  prazo_ate: 'Prazo até', data_pagamento: 'Data do pagamento', avec_cents: 'No Avec', titulos_cents: 'Em títulos',
  falta_cents: 'Falta', quantidade_avec: 'Pagamentos no Avec', categoria: 'Categoria', categoria_nome: 'Categoria',
  regra: 'Regra', ultimo_valor_cents: 'Último valor', dia_previsto: 'Dia previsto', esperado: 'Esperado',
  bruto_cents: 'Vendido (bruto)', taxa_cents: 'Taxa', liquido_cents: 'Líquido', liquido_min_cents: 'Líquido mínimo',
  liquido_max_cents: 'Líquido máximo', qtd: 'Pagamentos', estimado: 'Estimado', dias_procurados: 'Dias procurados',
  semana_de: 'Semana de', semana_ate: 'Semana até', esperado_cents: 'Esperado', esperado_min_cents: 'Esperado (mín.)',
  esperado_max_cents: 'Esperado (máx.)', creditado_cents: 'Creditado', diferenca_cents: 'Diferença',
  dia_venda: 'Dia da venda', valores_procurados: 'Valores procurados', vendido_cartao_cents: 'Vendido no cartão',
  creditado_cartao_cents: 'Creditado de cartão', taxa_esperada_cents: 'Taxa esperada', taxa_esperada_max_cents: 'Taxa esperada (máx.)',
  diferenca_nao_explicada_cents: 'Diferença não explicada', ultimo_dia_coberto: 'Último dia com extrato',
  dias_sem: 'Dias sem extrato', relatorio: 'Relatório do Avec', status: 'Situação da coleta', erro: 'Erro',
  tentativas: 'Tentativas', perda_cents: 'Perda', total_removido_cents: 'Total removido', depois_do_dia: 'Mudou depois do dia',
  comparado_com: 'Comparado com', conta_id: 'Conta do extrato', modo: 'Tipo de coleta',
  produto: 'Produto', qtd_acima: 'PIX acima do contrato', taxa_contratada_cents: 'Taxa pelo contrato',
  taxa_cobrada_cents: 'Taxa cobrada', pct_contratado: 'Taxa do contrato (%)', pct_cobrado: 'Taxa cobrada (%)',
  // 38 (24/10/2026): cartão parcelado acima do permitido
  parcelas: 'Parcelas', parcelas_max: 'Máximo de parcelas permitido', taxa_a_mais_cents: 'Taxa a mais que no máximo permitido',
};
const VALOR_TEXTO = {
  ultima_foto_com_pagamento: 'a última foto em que a comanda tinha pagamento (ela tinha sumido e voltou)',
  avaliacao_hoje: 'avaliação da foto de hoje falhando', avaliacao_diaria: 'avaliação diária falhando',
  avaliacao_apagadas: 'pista de PIX das comandas apagadas falhando',
  diaria_atrasada: 'diária atrasada', diaria_incompleta: 'unidade fora da diária',
  comandas_suspeitas_hoje: 'comandas suspeitas (foto de hoje)', comandas_suspeitas_diaria: 'comandas suspeitas (coleta diária)',
  comandas_suspeitas_mensal: 'comandas suspeitas (coleta mensal)',
  // cópia dos dados (financeiro-014, 22/10/2026)
  copia_export_nunca: 'cópia diária ainda não rodou', copia_export_atrasada: 'cópia diária atrasada',
  copia_export_falhou: 'cópia diária falhou ou veio incompleta', copia_manifesto: 'cópia diária não confere',
  copia_linhas_sumiram: 'linhas sumidas desde a cópia anterior', titulo_apagado: 'conta a pagar apagada de verdade',
  copia_fora_parada: 'cópia fora do sistema parada', copia_fora_falhou: 'cópia fora do sistema falhou',
  copia_anexos: 'comprovantes sem cópia fora do sistema', copia_sem_conferencia: 'vigilância da cópia sem leitura',
  // revisão 1 (22/10/2026): a cópia fora que nunca rodou e a cópia diária dos cadastros
  copia_fora_nunca: 'cópia fora do sistema ainda não rodou', backup_diario_falhou: 'cópia diária dos cadastros não saiu inteira',
  backup_diario_atrasado: 'cópia diária dos cadastros atrasada',
  // 22/10/2026 (cópia só no Supabase, bucket separado fk-copia)
  armazenamento_principal_sumiu: 'armazenamento principal do sistema sumiu', copia_armazenamento_sumiu: 'armazenamento da cópia sumiu',
  copia_arquivos: 'cópia dos arquivos atrasada ou com falha', copia_comprovantes: 'comprovante perdido no armazenamento',
  // 22/10/2026 (revisão da cópia, rodada 1)
  copia_export_lenta: 'cópia diária perto do limite de tempo',
};
// Mostrados em blocos próprios do detalhe (ou referência interna)
const CAMPOS_ESPECIAIS = new Set(['ref', 'antes', 'depois', 'movimentos', 'motivos', 'titulo', 'linhas', 'forma_pagamento', 'pista_pix']);
const RE_DATAHORA = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function valorCampo(chave, v) {
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  if (/_cents$/.test(chave)) return Array.isArray(v) ? v.map(x => fmtCentavos(x)).join(' ou ') : fmtCentavos(v);
  if (Array.isArray(v)) return v.map(x => (/^\d{4}-\d{2}-\d{2}$/.test(String(x)) ? dataBR(x) : String(x))).join(', ');
  const s = String(v);
  if (/_em$/.test(chave) || RE_DATAHORA.test(s)) return RE_DATAHORA.test(s) ? dataHoraBR(s) : s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return dataBR(s);
  if (chave === 'competencia') return competenciaBR(s);
  if (/^minutos/.test(chave)) return `${s} min`;
  if (chave === 'horas_aberto') return `${s} h`;
  if (chave === 'dias_depois' || chave === 'dias_sem') return `${s} dia(s)`;
  if (chave === 'formas_na_foto' || chave === 'formas') return s;
  return VALOR_TEXTO[s] || s;
}

/**
 * dados do alerta → [{ chave, rotulo, valor }] para a lista de detalhes.
 * Objetos de 1 nível (ex.: esperado) viram "Esperado · Líquido". Listas de
 * objetos e a referência interna ficam de fora (têm bloco próprio).
 */
export function camposDoAlerta(dados) {
  const out = [];
  if (!dados || typeof dados !== 'object') return out;
  for (const [k, v] of Object.entries(dados)) {
    if (CAMPOS_ESPECIAIS.has(k) || v === null || v === undefined || v === '') continue;
    if (Array.isArray(v)) {
      if (!v.length || v.some(x => x && typeof x === 'object')) continue;
      if (k === 'formas_na_foto' || k === 'formas') {
        out.push({ chave: k, rotulo: ROTULO_CAMPO[k] || humanizar(k), valor: v.map(textoGrupoOuForma).join(', ') });
        continue;
      }
      out.push({ chave: k, rotulo: ROTULO_CAMPO[k] || humanizar(k), valor: valorCampo(k, v) });
      continue;
    }
    if (typeof v === 'object') {
      for (const [k2, v2] of Object.entries(v)) {
        if (v2 === null || v2 === undefined || v2 === '' || (typeof v2 === 'object' && !Array.isArray(v2))) continue;
        if (Array.isArray(v2) && v2.some(x => x && typeof x === 'object')) continue;
        out.push({ chave: `${k}.${k2}`, rotulo: `${ROTULO_CAMPO[k] || humanizar(k)} · ${ROTULO_CAMPO[k2] || humanizar(k2)}`, valor: valorCampo(k2, v2) });
      }
      continue;
    }
    out.push({ chave: k, rotulo: ROTULO_CAMPO[k] || humanizar(k), valor: valorCampo(k, v) });
  }
  return out;
}
const textoGrupoOuForma = (g) => GRUPO_PARA[g] || (FORMA_TXT[g] || humanizar(g)).toLowerCase();

// ─── Comanda apagada: forma de pagamento e pista de PIX (07/10/2026) ───────
// Pedido do dono: "as comandas que foram apagadas precisamos saber a forma de
// pagamento que era a comanda, para saber se foi dinheiro ou PIX". O servidor
// manda dados.forma_pagamento ({estado, formas:[{forma, valor_cents}], foto_em,
// criada_em, apagada_em}) e dados.pista_pix ({estado, creditos:[{dia, hora,
// valor_cents, via}]}) — sem nome; aqui só se escreve para a tela.
const FORMAS_SENSIVEIS = new Set(['dinheiro', 'pix', 'transferencia']);

/** forma_pagamento → chips [{ forma, rotulo, valor_cents, sensivel }] (só quando a foto viu). */
export function formasDaApagada(fp) {
  if (!fp || typeof fp !== 'object' || fp.estado !== 'foto' || !Array.isArray(fp.formas)) return [];
  return fp.formas.filter(x => x && Number.isFinite(Number(x.valor_cents))).map(x => ({
    forma: x.forma, rotulo: FORMA_TXT[x.forma] || humanizar(x.forma), valor_cents: Number(x.valor_cents), sensivel: FORMAS_SENSIVEIS.has(x.forma),
  }));
}

// "às 14:20" no mesmo dia de referência; "em 06/10 às 21:00" noutro dia
function quandoNoDia(iso, refIso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  const ref = new Date(refIso);
  const mesmoDia = !Number.isNaN(ref.getTime()) && diaSP(d) === diaSP(ref);
  return mesmoDia ? `às ${horaSP(iso)}` : `em ${diaMes(diaSP(d))} às ${horaSP(iso)}`;
}

/**
 * forma_pagamento → texto (sem o "Forma:"). curto = para o cartão da lista.
 * "Dinheiro R$ 150,00 · PIX R$ 50,00 (foto de 07/10/2026 14:20)" |
 * "não registrada — a comanda foi apagada antes da foto do sistema (criada às 14:02, apagada às 14:09)" |
 * "não registrada — anterior ao início da vigilância" | null (sem o dado: alerta antigo)
 */
export function textoFormaPagamento(fp, { curto = false } = {}) {
  if (!fp || typeof fp !== 'object') return null;
  const formas = formasDaApagada(fp);
  if (formas.length) {
    const base = formas.map(x => `${x.rotulo} ${fmtCentavos(x.valor_cents)}`).join(' · ');
    return curto || !fp.foto_em ? base : `${base} (foto de ${dataHoraBR(fp.foto_em)})`;
  }
  if (fp.estado === 'sem_detalhe') return 'não identificada';
  if (fp.estado === 'anterior_vigilancia') return curto ? 'não registrada (antes da vigilância)' : 'não registrada — anterior ao início da vigilância';
  if (fp.estado === 'nao_registrada') {
    if (curto) return 'não registrada';
    const ref = fp.apagada_em || fp.criada_em;
    const quando = [fp.criada_em ? `criada ${quandoNoDia(fp.criada_em, ref)}` : null, fp.apagada_em ? `apagada ${quandoNoDia(fp.apagada_em, ref)}` : null]
      .filter(Boolean).join(', ');
    return `não registrada — a comanda foi apagada antes da foto do sistema${quando ? ` (${quando})` : ''}`;
  }
  return null;
}

/**
 * Título do cartão da comanda apagada SEM o " · Forma: …" (a forma e a pista
 * aparecem em chips logo abaixo). Sem os dados novos (alerta antigo), o título fica inteiro.
 */
export function tituloDoCartao(a) {
  const t = String(a?.titulo || '');
  if (a?.tipo !== 'comanda_apagada' || !a?.forma_pagamento) return t;
  const i = t.indexOf(' · Forma: ');
  return i > 0 ? t.slice(0, i) : t;
}

/**
 * pista_pix → { tom: 'alerta'|'neutro', texto } | null.
 * Encontrada: "PIX de R$ 50,00 recebido às 14:32 de um pagador com o mesmo nome do cliente".
 */
export function textoPistaPix(pp, { diaRef = null } = {}) {
  if (!pp || typeof pp !== 'object') return null;
  const creditos = Array.isArray(pp.creditos) ? pp.creditos.filter(c => c && Number(c.valor_cents) > 0) : [];
  if (pp.estado === 'encontrada' && creditos.length) {
    const c = creditos[0];
    const hora = c.hora ? String(c.hora).slice(0, 5) : null;
    const quando = hora ? (diaRef && c.dia === diaRef ? `às ${hora}` : `em ${diaMes(c.dia)} às ${hora}`) : `em ${diaMes(c.dia)}`;
    return {
      tom: 'alerta',
      texto: `${c.via === 'chave' ? 'PIX na chave' : 'PIX'} de ${fmtCentavos(c.valor_cents)} recebido ${quando} de um pagador com o mesmo nome do cliente`
        + `${c.via === 'maquininha' ? ' (maquininha)' : ''}${creditos.length > 1 ? ` (e mais ${creditos.length - 1})` : ''}`,
    };
  }
  const TXT = {
    procurada: 'Nenhum PIX de um pagador com o mesmo nome do cliente no extrato desses dias.',
    cliente_com_viva: 'O cliente tem outra comanda paga por PIX nesses dias (provavelmente a comanda refeita): o PIX com o mesmo nome no extrato não foi atribuído a esta.',
    sem_extrato: 'O extrato desses dias ainda não foi enviado: quando chegar, a auditoria procura um PIX com o mesmo nome do cliente.',
    sem_nome: 'O Avec não trouxe o nome do cliente: não dá para procurar o PIX dele no extrato.',
    nao_verificada: 'O extrato não pôde ser lido para procurar um PIX com o mesmo nome do cliente.',
  };
  return TXT[pp.estado] ? { tom: 'neutro', texto: TXT[pp.estado] } : null;
}

/**
 * Pista de PIX vigente do alerta: a da última avaliação, menos quando ela não
 * conseguiu ler o extrato e uma anterior tinha achado o PIX (a gravidade não
 * desce — a evidência também não some da tela).
 */
export function pistaVigente(a) {
  const ehO = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
  const ult = ehO(a?.dados_ultimo) && ehO(a.dados_ultimo.pista_pix) ? a.dados_ultimo.pista_pix : null;
  const pri = ehO(a?.dados) && ehO(a.dados.pista_pix) ? a.dados.pista_pix : null;
  if (ehO(a?.dados_ultimo) && ehO(a.dados_ultimo.forma_pagamento)) {
    return ult?.estado === 'nao_verificada' && pri?.estado === 'encontrada' ? pri : ult;
  }
  return pri;
}

// ─── Recebimentos (agenda + conferência com o extrato) ─────────────────────
// 09/10/2026 (rotina da unidade): os textos vêm do dicionário (utils/finRotulos.js) —
// com FIN_TRILHO_UNIDADE: Bateu / Aguardando / Divergente; sem: FECHADO / AGUARDANDO / EXCEÇÃO
const textoEstadoDia = (chave, antigo) => (typeof ROTULOS?.estadoDia?.[chave] === 'string' && ROTULOS.estadoDia[chave]) || antigo;
export const ESTADO_DIA = {
  fechado: { texto: textoEstadoDia('fechado', 'FECHADO'), classe: 'bg-success-soft text-success-text border-success/20' },
  aguardando: { texto: textoEstadoDia('aguardando', 'AGUARDANDO'), classe: 'bg-slate-100 text-slate-600 border-slate-200' },
  excecao: { texto: textoEstadoDia('excecao', 'EXCEÇÃO'), classe: 'bg-danger-soft text-danger-text border-danger/30' },
  sem_venda: { texto: 'SEM VENDA', classe: 'bg-slate-50 text-slate-400 border-slate-200' },
  sem_extrato: { texto: 'SEM EXTRATO', classe: 'bg-warning-soft text-warning-text border-warning/30' },
};
/** fechado → 'FECHADO'|'Bateu', aguardando → 'AGUARDANDO'|'Aguardando', excecao → 'EXCEÇÃO'|'Divergente', sem_venda → 'SEM VENDA' */
export const estadoDiaTexto = (estado) => ESTADO_DIA[estado]?.texto || (estado ? String(estado).toUpperCase() : '—');
export const estadoDiaClasse = (estado) => ESTADO_DIA[estado]?.classe || 'bg-slate-100 text-slate-600 border-slate-200';

export const ESTADO_PIX = {
  casado: { texto: 'CAIU', classe: 'bg-success-soft text-success-text border-success/20' },
  aguardando: { texto: 'AGUARDANDO', classe: 'bg-slate-100 text-slate-600 border-slate-200' },
  sem_credito: { texto: 'SEM CRÉDITO', classe: 'bg-danger-soft text-danger-text border-danger/30' },
  empate: { texto: 'EMPATE (revisar)', classe: 'bg-warning-soft text-warning-text border-warning/30' },
};
export const estadoPixTexto = (e) => ESTADO_PIX[e]?.texto || (e ? String(e).toUpperCase() : '—');
export const estadoPixClasse = (e) => ESTADO_PIX[e]?.classe || 'bg-slate-100 text-slate-600 border-slate-200';

// Semana (seg–dom) e mês fechado, como o servidor devolve (casarRecebimentos)
const CLS_NEUTRO = 'bg-slate-100 text-slate-600 border-slate-200';
export const ESTADO_SEMANA = {
  ok: { texto: 'OK', classe: 'bg-success-soft text-success-text border-success/20' },
  falta: { texto: 'FALTOU', classe: 'bg-danger-soft text-danger-text border-danger/30' },
  sobra: { texto: 'SOBROU', classe: 'bg-warning-soft text-warning-text border-warning/30' },
  incompleta: { texto: 'INCOMPLETA', classe: CLS_NEUTRO },
  aguardando: { texto: 'AGUARDANDO', classe: CLS_NEUTRO },
};
export const ESTADO_MES = {
  ok: { texto: 'OK', classe: 'bg-success-soft text-success-text border-success/20' },
  acima: { texto: 'ACIMA', classe: 'bg-danger-soft text-danger-text border-danger/30' },
  incompleto: { texto: 'INCOMPLETO', classe: CLS_NEUTRO },
  aguardando: { texto: 'AGUARDANDO', classe: CLS_NEUTRO },
};
export const estadoSemana = (e) => ESTADO_SEMANA[e] || { texto: e ? String(e).toUpperCase() : '—', classe: CLS_NEUTRO };
export const estadoMes = (e) => ESTADO_MES[e] || { texto: e ? String(e).toUpperCase() : '—', classe: CLS_NEUTRO };

const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const primeiro = (...vs) => { for (const v of vs) { const n = num(v); if (n !== null) return n; } return null; };
const ehCartao = (p) => !p || p === 'cartao' || p === 'credito' || p === 'debito';

/**
 * Linhas da tabela "por dia previsto × unidade" (só cartão). A conferência
 * (casarRecebimentos) manda quando existe; sem extrato fica só a agenda e o
 * estado vira SEM EXTRATO. Taxa mín./máx. sai da faixa do líquido.
 * → [{ chave, unit_id, dia_previsto, qtd, bruto_cents, taxa_cents, taxa_min_cents, taxa_max_cents,
 *      liquido_cents, liquido_min_cents, liquido_max_cents, creditado_cents|null, diferenca_cents|null,
 *      estado, estimado }]  (dia mais novo primeiro; unidades na ordem da DRE)
 */
export function linhasRecebimentos({ agenda, conferencia } = {}) {
  const porChave = new Map();
  const chave = (u, d) => `${u}|${d}`;
  for (const d of agenda?.porDia || []) {
    if (!ehCartao(d?.produto)) continue;
    const dia = d.dia_previsto || d.dia;
    if (!d?.unit_id || !dia) continue;
    const k = chave(d.unit_id, dia);
    const prev = porChave.get(k);
    if (prev) {
      // agenda separada por débito/crédito: soma no mesmo dia
      for (const c of ['bruto_cents', 'taxa_cents', 'liquido_cents', 'liquido_min_cents', 'liquido_max_cents', 'qtd']) {
        prev.agenda[c] = (num(prev.agenda[c]) || 0) + (num(d[c]) || 0);
      }
      prev.agenda.estimado = prev.agenda.estimado || !!d.estimado;
    } else {
      porChave.set(k, { unit_id: d.unit_id, dia_previsto: dia, agenda: { ...d }, conf: null });
    }
  }
  const temConferencia = !!conferencia && Array.isArray(conferencia.dias);
  for (const c of (temConferencia ? conferencia.dias : [])) {
    if (!ehCartao(c?.produto)) continue;
    const dia = c.dia_previsto || c.dia;
    if (!c?.unit_id || !dia) continue;
    const k = chave(c.unit_id, dia);
    const prev = porChave.get(k);
    if (prev) prev.conf = c;
    else porChave.set(k, { unit_id: c.unit_id, dia_previsto: dia, agenda: {}, conf: c });
  }
  const linhas = [];
  for (const [k, { unit_id, dia_previsto, agenda: a, conf: c }] of porChave) {
    const esp = (c && typeof c.esperado === 'object' && c.esperado) || {};
    const bruto = primeiro(c?.bruto_cents, esp.bruto_cents, c?.vendido_cents, a.bruto_cents);
    const liquido = primeiro(c?.liquido_cents, esp.liquido_cents, c?.esperado_cents, a.liquido_cents);
    const liqMin = primeiro(c?.liquido_min_cents, esp.liquido_min_cents, c?.esperado_min_cents, a.liquido_min_cents, liquido);
    const liqMax = primeiro(c?.liquido_max_cents, esp.liquido_max_cents, c?.esperado_max_cents, a.liquido_max_cents, liquido);
    const taxa = primeiro(c?.taxa_cents, esp.taxa_cents, a.taxa_cents, bruto !== null && liquido !== null ? bruto - liquido : null);
    // 12/10/2026 (REGRA LINEAR): dia cujo PRÓPRIO dia do depósito está fora do
    // extrato não tem "creditado R$ 0,00" — fica "—" e não conta como dia com
    // extrato. Com o dia no arquivo e só a folga faltando (coberto_dia), o que já
    // caiu aparece (aguardando) e a diferença espera a folga.
    const comExtrato = !!c && (c.coberto !== false || c.coberto_dia === true);
    const cobreFolga = !!c && c.coberto !== false;
    const creditado = comExtrato ? primeiro(c.creditado_cents, c.creditado) : null;
    const diferenca = cobreFolga ? primeiro(c.diferenca_cents, creditado !== null && liquido !== null ? creditado - liquido : null) : null;
    const estado = c?.estado || (temConferencia ? 'aguardando' : 'sem_extrato');
    linhas.push({
      chave: k, unit_id, dia_previsto,
      qtd: primeiro(c?.qtd, esp.qtd, a.qtd) || 0,
      bruto_cents: bruto, taxa_cents: taxa,
      taxa_min_cents: primeiro(a.taxa_min_cents, bruto !== null && liqMax !== null ? bruto - liqMax : taxa),
      taxa_max_cents: primeiro(a.taxa_max_cents, bruto !== null && liqMin !== null ? bruto - liqMin : taxa),
      liquido_cents: liquido, liquido_min_cents: liqMin, liquido_max_cents: liqMax,
      creditado_cents: creditado, diferenca_cents: diferenca, estado,
      estimado: !!(c?.estimado ?? esp.estimado ?? a.estimado),
      // nada caiu no prazo (com extrato cobrindo) / vendas de antes do período não lidas
      nao_caiu: !!c?.nao_caiu,
      incompleto: !!(c?.incompleto || a.incompleto),
    });
  }
  return linhas.sort((x, y) => (x.dia_previsto < y.dia_previsto ? 1 : x.dia_previsto > y.dia_previsto ? -1 : ordemUnidade(x.unit_id) - ordemUnidade(y.unit_id)));
}

/** Totais das linhas (só o que tem número). */
export function totaisRecebimentos(linhas = []) {
  const t = { bruto_cents: 0, taxa_cents: 0, taxa_min_cents: 0, taxa_max_cents: 0, liquido_cents: 0, creditado_cents: 0, dias_com_extrato: 0, excecoes: 0, estimado: false };
  for (const l of linhas) {
    t.bruto_cents += l.bruto_cents || 0;
    t.taxa_cents += l.taxa_cents || 0;
    t.taxa_min_cents += l.taxa_min_cents || 0;
    t.taxa_max_cents += l.taxa_max_cents || 0;
    t.liquido_cents += l.liquido_cents || 0;
    if (l.creditado_cents !== null) { t.creditado_cents += l.creditado_cents; t.dias_com_extrato += 1; }
    if (l.estado === 'excecao') t.excecoes += 1;
    if (l.estimado) t.estimado = true;
  }
  return t;
}

/**
 * Bloco PIX: conferência um a um (quando há extrato) ou só o total esperado da
 * agenda. → { itens: [{unit_id, dia_venda, valor_cents, estado}], contagem: {estado: n}, esperado_cents, qtd }
 */
export function resumoPix({ agenda, conferencia } = {}) {
  const itens = [];
  const contagem = {};
  for (const p of conferencia?.pix || []) {
    const estado = p.estado || 'aguardando';
    contagem[estado] = (contagem[estado] || 0) + 1;
    itens.push({
      unit_id: p.unit_id, dia_venda: p.dia_venda || p.dia, numero: p.numero ?? null, produto: p.produto || 'pix',
      valor_cents: primeiro(p.valor_cents, p.bruto_cents), estado,
    });
  }
  let esperado = 0;
  let qtd = 0;
  for (const d of agenda?.porDia || []) {
    if (d?.produto !== 'pix' && d?.produto !== 'transferencia') continue;
    esperado += num(d.bruto_cents) || 0;
    qtd += num(d.qtd) || 0;
  }
  itens.sort((a, b) => {
    const pa = a.estado === 'sem_credito' || a.estado === 'empate' ? 0 : 1;
    const pb = b.estado === 'sem_credito' || b.estado === 'empate' ? 0 : 1;
    if (pa !== pb) return pa - pb;
    return String(b.dia_venda || '').localeCompare(String(a.dia_venda || ''));
  });
  return { itens, contagem, esperado_cents: esperado, qtd, conferido: !!(conferencia && Array.isArray(conferencia.pix)) };
}

/**
 * Resumo do mês por unidade, uma linha de CARTÃO (crédito + débito) e uma de
 * PIX quando houver. A agenda (resumoMes) traz o mês pelo dia da venda, com
 * `por_produto`; a conferência (meses, só cartão, mês fechado e com extrato)
 * manda na linha de cartão quando existe — com creditado, diferença não
 * explicada e o estado (ok / acima / incompleto / aguardando).
 * → [{ chave, unit_id, competencia, produto: 'cartao'|'pix', vendido_cents, taxa_cents, taxa_max_cents,
 *      pct, creditado_cents|null, diferenca_nao_explicada_cents|null, estado|null, estimado, sem_taxa_qtd }]
 */
export function linhasResumoMes({ agenda, conferencia } = {}) {
  const conf = new Map();
  for (const m of conferencia?.meses || []) {
    const comp = m.competencia || m.mes;
    if (m?.unit_id && comp) conf.set(`${m.unit_id}|${comp}`, m);
  }
  const out = [];
  const pctDe = (taxa, vendido) => (vendido ? Math.round((taxa || 0) * 10000 / vendido) / 100 : null);
  const somaProdutos = (pp, nomes) => {
    const s = { bruto_cents: 0, taxa_cents: 0, taxa_max_cents: 0, qtd: 0, tem: false };
    for (const n of nomes) {
      const x = pp?.[n];
      if (!x) continue;
      s.tem = true;
      s.bruto_cents += num(x.bruto_cents) || 0;
      s.taxa_cents += num(x.taxa_cents) || 0;
      s.taxa_max_cents += num(x.taxa_max_cents ?? x.taxa_cents) || 0;
      s.qtd += num(x.qtd) || 0;
    }
    return s;
  };
  const vistos = new Set();
  const linhaCartao = (unit_id, comp, m, c) => {
    const pp = m && m.por_produto && typeof m.por_produto === 'object' ? somaProdutos(m.por_produto, ['credito', 'debito', 'cartao']) : null;
    const vendido = primeiro(c?.vendido_cartao_cents, pp?.tem ? pp.bruto_cents : null, m?.vendido_cartao_cents, m?.bruto_cents);
    const taxa = primeiro(c?.taxa_esperada_cents, pp?.tem ? pp.taxa_cents : null, m?.taxa_esperada_cents, m?.taxa_cents);
    const taxaMax = primeiro(c?.taxa_esperada_max_cents, pp?.tem ? pp.taxa_max_cents : null, m?.taxa_max_cents, taxa);
    return {
      chave: `${unit_id}|${comp}|cartao`, unit_id, competencia: comp, produto: 'cartao',
      vendido_cents: vendido, taxa_cents: taxa, taxa_max_cents: taxaMax, pct: pctDe(taxa, vendido),
      creditado_cents: c ? primeiro(c.creditado_cartao_cents, c.creditado_cents) : null,
      diferenca_nao_explicada_cents: c ? primeiro(c.diferenca_nao_explicada_cents) : null,
      estado: c?.estado || null,
      estimado: !!(c?.estimado || m?.estimado || (num(m?.estimado_bruto_cents) || 0) > 0),
      sem_taxa_qtd: num(m?.sem_taxa_qtd) || 0,
    };
  };
  for (const m of agenda?.resumoMes || []) {
    if (!m?.unit_id) continue;
    const comp = m.competencia || m.mes;
    const k = `${m.unit_id}|${comp}`;
    const c = conf.get(k) || null;
    vistos.add(k);
    out.push(linhaCartao(m.unit_id, comp, m, c));
    const pix = m.por_produto && typeof m.por_produto === 'object' ? somaProdutos(m.por_produto, ['pix']) : null;
    if (pix?.tem && pix.bruto_cents) {
      out.push({
        chave: `${k}|pix`, unit_id: m.unit_id, competencia: comp, produto: 'pix',
        vendido_cents: pix.bruto_cents, taxa_cents: pix.taxa_cents, taxa_max_cents: pix.taxa_max_cents, pct: pctDe(pix.taxa_cents, pix.bruto_cents),
        creditado_cents: null, diferenca_nao_explicada_cents: null, estado: null, estimado: false, sem_taxa_qtd: 0,
      });
    }
  }
  for (const [k, c] of conf) if (!vistos.has(k)) out.push(linhaCartao(c.unit_id, c.competencia || c.mes, null, c));
  return out.sort((a, b) => (a.competencia < b.competencia ? 1 : a.competencia > b.competencia ? -1
    : ordemUnidade(a.unit_id) - ordemUnidade(b.unit_id) || (a.produto === 'cartao' ? -1 : 1)));
}

/** Cobertura do extrato [{de, ate}] × período → 'total' | 'parcial' | 'nenhuma'. */
export function coberturaNoPeriodo(cobertura, de, ate) {
  const faixas = (cobertura || []).filter(f => f && f.de && f.ate).map(f => [f.de, f.ate]).sort();
  if (!faixas.length || !de || !ate) return 'nenhuma';
  // junta faixas encostadas e mede quantos dias do período estão cobertos
  let cobertos = 0;
  let cursor = null;
  for (const [fde, fate] of faixas) {
    const ini = fde > de ? fde : de;
    const fim = fate < ate ? fate : ate;
    if (ini > fim) continue;
    const comeco = cursor && cursor >= ini ? somarDias(cursor, 1) : ini;
    if (comeco <= fim) cobertos += diasEntre(comeco, fim) + 1;
    if (!cursor || fim > cursor) cursor = fim;
  }
  const total = diasEntre(de, ate) + 1;
  if (cobertos <= 0) return 'nenhuma';
  return cobertos >= total ? 'total' : 'parcial';
}

/** Período padrão de Recebimentos: últimos 14 dias até hoje + 3 (o que vai cair). */
export function periodoPadraoRecebimentos(hoje) {
  return { de: somarDias(hoje, -14), ate: somarDias(hoje, 3) };
}
/** Período válido? → null ou a mensagem (máx. 93 dias, como o servidor). */
export function validarPeriodo({ de, ate } = {}, maxDias = 93) {
  if (!RE_DIA.test(String(de || '')) || !RE_DIA.test(String(ate || ''))) return 'Escolha as duas datas do período.';
  const n = diasEntre(de, ate);
  if (n < 0) return 'A data inicial é depois da final.';
  if (n + 1 > maxDias) return `Período grande demais: no máximo ${maxDias} dias.`;
  return null;
}

// ─── Fechamento do dia anterior (12/10/2026) ───────────────────────────────
// "entradas × o que caiu na conta × o que tem de caixa" (dono). O servidor
// (op=fechamento-dia) manda os números prontos; aqui só rótulo e cor (as
// mesmas classes de ESTADO_DIA / ESTADO_PIX: nada de visual novo).
const CLS_OK = 'bg-success-soft text-success-text border-success/20';
const CLS_DIV = 'bg-danger-soft text-danger-text border-danger/30';
const CLS_AVISO = 'bg-warning-soft text-warning-text border-warning/30';
export const ESTADO_FECHAMENTO = {
  bateu: { texto: 'Bateu', classe: CLS_OK },
  divergente: { texto: 'Divergente', classe: CLS_DIV },
  aguardando: { texto: 'Aguardando', classe: CLS_NEUTRO },
  aguardando_prazo: { texto: 'Aguardando o prazo', classe: CLS_NEUTRO },
  aguardando_extrato: { texto: 'Aguardando extrato', classe: CLS_AVISO },
  sem_venda: { texto: 'Sem venda', classe: 'bg-slate-50 text-slate-400 border-slate-200' },
  '?': { texto: 'Não deu para conferir', classe: CLS_AVISO },
  // gaveta
  falta: { texto: 'Falta', classe: CLS_DIV },
  sobra: { texto: 'Sobra', classe: CLS_AVISO },
  sem_contagem: { texto: 'Sem contagem', classe: CLS_AVISO },
  sem_movimento: { texto: 'Sem movimento', classe: 'bg-slate-50 text-slate-400 border-slate-200' },
  // PIX um a um (casarRecebimentos)
  casado: { texto: 'Casado', classe: CLS_OK },
  sem_credito: { texto: 'Sem crédito', classe: CLS_DIV },
};
/** estado (do dia, do depósito, do PIX ou da gaveta) → { texto, classe }; desconhecido → "Não deu para conferir". */
export const estadoFechamento = (e) => ESTADO_FECHAMENTO[e] || ESTADO_FECHAMENTO['?'];

/** Motivo de pendência do dia (resumo da régua) → texto curto. */
export const MOTIVO_FECHAMENTO_TXT = {
  stone: 'depósito da Stone não bateu', pix: 'PIX sem crédito', gaveta_falta: 'falta dinheiro na gaveta',
  gaveta_sobra: 'sobra dinheiro na gaveta', sem_contagem: 'gaveta sem contagem',
};

/** Linhas da tabela "Vendido no Avec por forma" (vendido de op=fechamento-dia; crédito aberto por parcelas). */
export function linhasVendido(vendido) {
  const f = vendido?.formas || {};
  const out = [];
  const add = (chave, rotulo, s) => { if (s && (Number(s.valor_cents) || Number(s.qtd))) out.push({ chave, rotulo, valor_cents: Number(s.valor_cents) || 0, qtd: Number(s.qtd) || 0 }); };
  add('dinheiro', 'Dinheiro', f.dinheiro);
  add('pix', 'PIX', f.pix);
  add('debito', 'Débito', f.debito);
  const parcelas = Object.keys(f.credito?.por_parcelas || {}).map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  for (const n of parcelas) add(`credito_${n}x`, `Crédito ${n}x`, f.credito.por_parcelas[String(n)]);
  add('transferencia', 'Transferência', f.transferencia);
  add('outras', 'Outras', f.outras);
  return out;
}

/** "sex 09/10" */
export const diaCurtoSemana = (dia) => `${diaSemanaCurto(dia)} ${diaMes(dia)}`.trim();

/** Lista de dias → "09/10, 10/10 e 12/10". */
export function listaDias(dias = []) {
  const ds = (dias || []).filter(Boolean).map(diaMes);
  if (ds.length <= 1) return ds.join('');
  return `${ds.slice(0, -1).join(', ')} e ${ds[ds.length - 1]}`;
}

/**
 * faturamento_periodo (op=recebimentos) → a frase do aviso acima da agenda:
 * "Faturamento do Avec de 01/10 a 12/10 (pelo dia da venda): R$ 137.957,80 =
 * cartão R$ 127.504,90 · PIX R$ 7.132,90 · dinheiro R$ 3.320,00." null = sem dado.
 */
export function textoFaturamentoPeriodo(fp) {
  if (!fp || typeof fp !== 'object' || fp.erro || !Number.isFinite(Number(fp.total_cents))) return null;
  const partes = [`cartão ${fmtCentavos(fp.cartao_cents ?? ((Number(fp.debito_cents) || 0) + (Number(fp.credito_cents) || 0)))}`,
    `PIX ${fmtCentavos(fp.pix_cents)}`, `dinheiro ${fmtCentavos(fp.dinheiro_cents)}`];
  if (Number(fp.transferencia_cents)) partes.push(`transferência ${fmtCentavos(fp.transferencia_cents)}`);
  if (Number(fp.outras_cents)) partes.push(`outras ${fmtCentavos(fp.outras_cents)}`);
  return `Faturamento do Avec de ${diaMes(fp.de)} a ${diaMes(fp.ate)} (pelo dia da venda): ${fmtCentavos(fp.total_cents)} = ${partes.join(' · ')}.`;
}

/**
 * Célula "Caiu na conta" das Entradas (venda × depósito): → { cartao: {texto, estado, junto}|null, pix: texto|null }.
 * deposito/pix vêm de op=entradas-avec (cruzarVendaDeposito); undefined = o cruzamento não carregou ('?').
 */
export function textoVendaDeposito(l) {
  const out = { cartao: null, pix: null };
  const cartao = (Number(l?.debito_cents) || 0) + (Number(l?.credito_cents) || 0);
  if (cartao > 0) {
    const d = l?.deposito;
    if (!d) out.cartao = { texto: 'Cartão: ?', estado: '?', junto: null, diferenca_cents: null };
    else {
      out.cartao = {
        texto: `Cartão: cai em ${diaMes(d.dia)}`, estado: d.estado,
        junto: Array.isArray(d.junto_com) && d.junto_com.length ? `junto com ${listaDias(d.junto_com)}` : null,
        diferenca_cents: d.estado === 'divergente' ? d.diferenca_cents ?? null : null,
      };
    }
  }
  const temPix = (Number(l?.pix_cents) || 0) + (Number(l?.transferencia_cents) || 0) > 0;
  if (temPix) {
    const p = l?.pix;
    if (!p) out.pix = 'PIX: ?';
    else if (p.estado === '?') out.pix = 'PIX: não deu para conferir';
    else out.pix = `PIX: ${p.caiu} de ${p.n} caíram${p.sem_credito ? ` · ${p.sem_credito} sem crédito` : ''}`;
  }
  return out;
}

/** Texto do chip do depósito na Entradas: "Bateu" | "Divergente −R$ 621,62" | "Aguardando…" */
export function chipDeposito(c) {
  if (!c) return null;
  const e = estadoFechamento(c.estado);
  if (c.estado === 'divergente' && Number.isFinite(Number(c.diferenca_cents))) {
    const v = Number(c.diferenca_cents);
    return { texto: `${e.texto} ${v < 0 ? '−' : '+'}${fmtCentavos(Math.abs(v))}`, classe: e.classe };
  }
  return e;
}

// ─── Extrato ────────────────────────────────────────────────────────────────
export const CLASSE_EXTRATO_TXT = {
  cartao: 'Recebível de cartão', pix_recebido: 'PIX recebido', transferencia_recebida: 'Transferência recebida',
  deposito_dinheiro: 'Depósito em dinheiro', estorno_credito: 'Estorno (crédito)', outro_credito: 'Outro crédito',
  pix_enviado: 'PIX enviado', boleto: 'Boleto pago', tarifa: 'Tarifa', transferencia_enviada: 'Transferência enviada',
  outro_debito: 'Outro débito',
};
export const textoClasse = (c) => CLASSE_EXTRATO_TXT[c] || humanizar(c);

/** '12345678' → '••••5678'; já mascarado passa; vazio → null. */
export function mascararConta(acctid) {
  const s = String(acctid ?? '').trim();
  if (!s) return null;
  if (s.startsWith('••••')) return s;
  const limpo = s.replace(/[^0-9A-Za-z]/g, '');
  return limpo ? `••••${limpo.slice(-4)}` : null;
}
/** '••••1234' → '1234' (para a pergunta "conta final 1234"). */
export const finalConta = (mascarado) => String(mascarado || '').replace(/[^0-9A-Za-z]/g, '').slice(-4) || '????';

/**
 * Forma de pagamento do formulário de Contas a Pagar pela classe do débito.
 * 09/10/2026: o vocabulário único (utils/finTituloCampos.formaDaClasse) —
 * tarifa agora é "Tarifa" (antes virava "Débito Automático").
 */
export const formaPagamentoDaClasse = (classe) => formaDaClasse(classe);

/**
 * Prefill do formulário EXISTENTE de Contas a Pagar (AccountModal do
 * Finance.jsx) a partir de um débito do extrato. Não grava nada: quem grava é
 * o "Salvar" do formulário.
 * 09/10/2026 (rotina financeira — "as informações não vão direito"):
 *   · descrição "<favorecido limpo> — <categoria> — <unidade> <mês>" (nunca o
 *     texto cru "… - Transferência | Pix" do banco);
 *   · conta = a Stone do débito ('stone:<final>');
 *   · forma pela classe (tarifa → Tarifa);
 *   · centro de custo = grupo da DRE da categoria (com o catálogo);
 *   · conta da TREE com o grupo "Oscar Freire + Jardins" ligado: o custo nas
 *     duas (custo_de do grupo) e a descrição com "Oscar Freire + Jardins".
 * opts: { catalogo (useCategoriasFin), grupo (finGrupos, só se LIGADO), mapa (nomes das unidades) }
 */
export function prefillDoExtrato(linha, conta = null, { catalogo = null, grupo = null, mapa = null } = {}) {
  const valor = Math.abs(Number(linha?.valor_cents) || 0) / 100;
  const cat = linha?.categoria_sugerida;
  const codigo = (cat && typeof cat === 'object' ? cat.codigo : cat) || '';
  const unitId = conta?.unit_id || linha?.unit_id || '';
  const doGrupo = !!(grupo && Array.isArray(grupo.unidades) && grupo.unidades.includes(unitId));
  const nomeCat = !codigo ? ''
    : (catalogo && !catalogo.reserva && typeof catalogo.rotulo === 'function' ? catalogo.rotulo(codigo) : '')
      || (cat && typeof cat === 'object' && cat.nome ? String(cat.nome) : '');
  const unidadeNome = !unitId ? '' : doGrupo ? grupo.unidades.map(u => nomeUnidade(u, mapa)).join(' + ') : nomeUnidade(unitId, mapa);
  const out = {
    unitId,
    titulo: descricaoDoExtrato({
      favorecido: linha?.favorecido, historico: linha?.historico, classe: linha?.classe,
      categoriaNome: nomeCat, unidadeNome, dia: linha?.dia,
    }),
    fornecedor: String(linha?.favorecido || '').trim(),
    categoria: codigo,
    valor: Number(valor.toFixed(2)),
    vencimento: linha?.dia || '',
    dataPagamento: linha?.dia || '',
    formaPagamento: formaDaClasse(linha?.classe),
    contaBancaria: conta ? contaBancariaDaConta(conta) : '',
    observacoes: `Lançado a partir do extrato Stone (linha #${linha?.id ?? '?'}).`,
  };
  const cc = codigo && catalogo ? centroCustoDaCategoria(codigo, catalogo) : null;
  if (cc) out.centroCusto = cc.valor;
  if (doGrupo && grupo.custo_de) out.custoDe = grupo.custo_de;
  return out;
}

// ─── "Como da última vez" (17/10/2026, atrás de FIN_ULTIMA_VEZ) ─────────────
// Pedido do dono: "o sistema deve reconhecer as contas já categorizadas para não
// precisar ficar lançando o que foi toda vez". O servidor manda, no débito
// pendente, ultima_vez.opcoes (1 ou 2; api/_lib/fin-extrato-casar.js
// sugerirUltimaVez): categoria, descrição-base (null = a automática), fornecedor
// como foi gravado, custo_de só da MESMA conta, rateio (unidades e modo), forma
// pela classe da linha e a competência só quando fixa (competencia_meses).
// Aqui só se monta o formulário EXISTENTE (AccountModal) e os textos da linha:
// quem grava é o Salvar dele. prefillDoExtrato não muda.
// O separador da descrição é o mesmo da automática (descricaoDoExtrato): o
// servidor tira a unidade e o mês antigos por ele (descricaoBase).
const SEPARADOR_DESCRICAO = ' — ';
const unidadesDe = (c) => [...new Set([c?.unit_id, ...(Array.isArray(c?.unidades) ? c.unidades : [])].filter(Boolean))];
const listaComE = (xs) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} e ${xs[xs.length - 1]}`);

/** Quem usa o custo, como o AccountModal escreve: o grupo ("Oscar Freire + Jardins"), a unidade escolhida ou a que paga. */
export function unidadeDoCusto({ unitId = '', custoDe = '', grupo = null, mapa = null } = {}) {
  const cd = String(custoDe ?? '').trim();
  if (grupo && cd && cd === grupo.custo_de && Array.isArray(grupo.unidades)) return grupo.unidades.map(u => nomeUnidade(u, mapa)).join(' + ');
  if (/^unit-/.test(cd)) return nomeUnidade(cd, mapa);
  return unitId ? nomeUnidade(unitId, mapa) : '';
}
/** A descrição: a que a pessoa digitou da última vez + a unidade e o mês deste débito; sem ela, a automática. */
export function descricaoUltimaVez({ linha = null, opcao = null, unidadeNome = '' } = {}) {
  const base = String(opcao?.descricao ?? '').replace(/\s+/g, ' ').trim();
  const un = String(unidadeNome || '').trim();
  if (base) {
    const quando = un ? [un, mesAno(linha?.dia)].filter(Boolean).join(' ') : '';
    return [base, quando].filter(Boolean).join(SEPARADOR_DESCRICAO).slice(0, 200);
  }
  return descricaoDoExtrato({
    favorecido: linha?.favorecido, historico: linha?.historico, classe: linha?.classe,
    categoriaNome: opcao?.categoria_nome || '', unidadeNome: un, dia: linha?.dia,
  });
}
/** A unidade que usa o custo: só da MESMA conta ('' = a que paga); de outra conta, o padrão da conta (o grupo). */
export function custoDeUltimaVez({ opcao = null, conta = null, grupo = null } = {}) {
  if (!opcao || opcao.rateio) return '';
  if (opcao.mesma_conta) return String(opcao.custo_de ?? '').trim();
  const doGrupo = !!(grupo && Array.isArray(grupo.unidades) && grupo.unidades.includes(conta?.unit_id));
  return doGrupo && grupo.custo_de ? grupo.custo_de : '';
}
/** 'AAAA-MM' da competência fixa (competencia_meses em relação ao mês do débito) ou null. */
export function competenciaUltimaVez(opcao, dia) {
  const n = Number(opcao?.competencia_meses);
  const m = /^(\d{4})-(\d{2})/.exec(String(dia || ''));
  if (opcao?.competencia_meses == null || !Number.isInteger(n) || n === 0 || !m) return null;
  const total = Number(m[1]) * 12 + (Number(m[2]) - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}
/** A linha de rastro da observação: "Preenchido como a conta de 08/10/2026 (linha #30)." */
export const rastroUltimaVez = (opcao) => RU.rastro(dataBR(opcao?.dia), opcao?.linha_id ?? null);

/** "rateio igual entre as 5 unidades" / "… entre Moema e Vila Olímpia" (na ordem da DRE). */
export function textoRateioUltimaVez(rateio, mapa = null) {
  const us = [...new Set((rateio?.unidades || []).filter(Boolean))].sort((a, b) => ordemUnidade(a) - ordemUnidade(b));
  if (us.length < 2) return '';
  const todas = us.length === ORDEM_UNIDADES.length && ORDEM_UNIDADES.every(u => us.includes(u));
  const quem = todas ? RU.todasUnidades(us.length) : listaComE(us.map(u => nomeUnidade(u, mapa)));
  if (rateio.aviso === 'por_valor') return RU.rateioValor(quem);
  return rateio.modo === 'porcentagem' ? RU.rateioPct(quem) : RU.rateioIgual(quem);
}
/** O custo da opção: o rateio, a unidade escolhida (só da mesma conta) ou nada (a que paga). */
export function textoCustoUltimaVez(opcao, { mapa = null, grupo = null } = {}) {
  if (opcao?.rateio) return textoRateioUltimaVez(opcao.rateio, mapa);
  const cd = String(opcao?.custo_de ?? '').trim();
  if (!opcao?.mesma_conta || !cd) return '';
  if (cd === 'nucleo') return RU.custoRede;
  if (grupo && cd === grupo.custo_de && Array.isArray(grupo.unidades)) return RU.custoDe(grupo.unidades.map(u => nomeUnidade(u, mapa)).join(' + '));
  return /^unit-/.test(cd) ? RU.custoDe(nomeUnidade(cd, mapa)) : '';
}
/** "prudutos feel king · Produtos para revenda": a descrição que a pessoa digitou e a categoria. */
export const textoOpcaoUltimaVez = (opcao) => [opcao?.descricao, opcao?.categoria_nome || opcao?.categoria].filter(Boolean).join(' · ');
/** "08/10 · TREE · rateio igual entre as 5 unidades · Boleto · 1× com este fornecedor" (semDia: sem o "08/10") */
export function detalheUltimaVez(opcao, { contaNome = '', mapa = null, grupo = null, base = null, intermediario = null, semDia = false } = {}) {
  return [
    semDia ? '' : diaMes(opcao?.dia), contaNome, textoCustoUltimaVez(opcao, { mapa, grupo }), opcao?.forma,
    Number(opcao?.vezes) > 0 ? RU.vezes(Number(opcao.vezes)) : '',
    base === 'contido' || base === 'nome_cortado' ? RU.nomeParecido : '',
    intermediario ? RU.intermediario : '',
  ].filter(Boolean).join(' · ');
}

/**
 * O formulário do "Lançar em Contas a Pagar" (base = o prefill de sempre, já com o débito)
 * preenchido como a última vez: categoria, descrição (_tituloAuto: false quando é a digitada),
 * fornecedor, unidade do custo, forma, o rateio (_rateio: unidades e modo, a conta do débito
 * paga), a competência fixa (_competencia) e a frase do quadro "Do extrato" (_ultimaVez).
 * Valor, data e conta continuam os do banco (travados no formulário). Não grava nada.
 * opts: { linha (o débito), conta (a do débito), grupo (finGrupos, só se LIGADO), mapa, contaNome (a conta da última vez) }
 */
export function prefillUltimaVez(base, opcao, { linha = null, conta = null, grupo = null, mapa = null, contaNome = '' } = {}) {
  const p = base && typeof base === 'object' ? { ...base } : {};
  if (!opcao || !opcao.categoria) return p;
  const unitId = opcao.mesma_conta && opcao.unit_id && unidadesDe(conta).includes(opcao.unit_id) ? opcao.unit_id : (p.unitId || conta?.unit_id || '');
  const rateio = opcao.rateio && Array.isArray(opcao.rateio.unidades) && opcao.rateio.unidades.length >= 2 ? opcao.rateio : null;
  const custoDe = rateio ? '' : custoDeUltimaVez({ opcao, conta, grupo });
  const competencia = competenciaUltimaVez(opcao, linha?.dia);
  const out = {
    ...p,
    unitId,
    categoria: opcao.categoria,
    titulo: descricaoUltimaVez({ linha, opcao, unidadeNome: unidadeDoCusto({ unitId, custoDe, grupo, mapa }) }),
    fornecedor: String(opcao.fornecedor || p.fornecedor || '').trim(),
    custoDe,
    formaPagamento: opcao.forma || p.formaPagamento || '',
    // 22/10/2026 (sem ruído, etapa C): a sugestão que não veio de uma conta anterior (comissão do Avec,
    // tarifa da Stone) não tem dia: o rastro é o do servidor e o quadro "Preenchido como a conta de" não aparece
    observacoes: [String(p.observacoes || '').trim(), opcao.dia ? rastroUltimaVez(opcao) : String(opcao.rastro || '').trim()].filter(Boolean).join(' '),
    ...(opcao.dia ? { _ultimaVez: { dia: opcao.dia || null, conta_nome: contaNome || '', linha_id: opcao.linha_id ?? null, aviso_rateio: rateio?.aviso || null, competencia } } : {}),
  };
  if (opcao.descricao) out._tituloAuto = false;
  if (rateio) out._rateio = { unidades: [...rateio.unidades], modo: rateio.modo || 'igual', pct_bp: rateio.pct_bp ? { ...rateio.pct_bp } : null, aviso: rateio.aviso || null };
  if (competencia) out._competencia = competencia;
  return out;
}

const ACAO_TXT = { vincular_titulo: 'Ligado a um título', ignorar: 'Sem título (com motivo)' };
/** situação da linha do extrato → { texto, pendente } */
export function textoSituacao(situacao) {
  if (!situacao || !situacao.acao || situacao.acao === 'desfazer') return { texto: 'Pendente', pendente: true };
  return { texto: ACAO_TXT[situacao.acao] || humanizar(situacao.acao), pendente: false };
}
export const FORCA_TXT = { forte: 'Muito provável', candidato: 'Possível' };

/** Aviso/descarte do OFX → texto (o servidor manda "codigo: texto" ou {motivo, quantidade, texto}). */
const AVISO_OFX_TXT = {
  sem_data: 'sem data', valor_invalido: 'valor inválido', valor_zero: 'valor zero', sem_bloco: 'bloco do OFX quebrado',
  tipo_e_sinal_divergem: 'tipo e sinal do valor divergem (vale o sinal do valor)',
  ultimo_dia_parcial: 'o arquivo foi gerado antes de o último dia acabar: esse dia só conta como conferido no próximo arquivo',
};
export function textoAvisoOfx(x) {
  if (x && typeof x === 'object') {
    const cod = x.motivo || x.codigo || x.tipo || '';
    const base = x.texto || AVISO_OFX_TXT[cod] || cod || 'aviso';
    return `${base}${x.quantidade ? ` (${x.quantidade})` : ''}`;
  }
  const s = String(x ?? '');
  const m = s.match(/^([a-z_]+):\s*(.+)$/);
  if (m) return m[2];
  return AVISO_OFX_TXT[s] || s;
}

/**
 * Prova de saldo (22/10/2026, lapidação etapa D): janela em que o saldo do banco não fecha com as linhas
 * (o arquivo veio só com as saídas) → "19/10 a 20/10 (faltam R$ 20.301,00)". Falta negativa = faltam saídas.
 */
export function textoAguardandoExtrato(j) {
  if (!j || !j.de || !j.ate) return '';
  const dias = j.de === j.ate ? diaMes(j.de) : `${diaMes(j.de)} a ${diaMes(j.ate)}`;
  const v = Number(j.falta_cents) || 0;
  return `${dias} (faltam ${fmtCentavos(Math.abs(v))}${v < 0 ? ' de saídas' : ''})`;
}
/** Os avisos da prova de saldo saem na caixa própria do envio, não na linha "Avisos" do OFX. */
const AVISOS_PROVA_SALDO = /^(saldo_nao_fecha|so_saidas|extrato_incompleto|prova_saldo):/;
export const ehAvisoProvaSaldo = (a) => typeof a === 'string' && AVISOS_PROVA_SALDO.test(a);

/** Junta as faixas de cobertura num texto: "01/09 a 30/09; 02/10 a 06/10". */
export function textoCobertura(cobertura) {
  const f = (cobertura || []).filter(x => x && x.de && x.ate);
  if (!f.length) return 'nenhum extrato enviado';
  return f.map(x => `${diaMes(x.de)} a ${diaMes(x.ate)}${x.ate.slice(0, 4) !== x.de.slice(0, 4) ? `/${x.ate.slice(0, 4)}` : ''}`).join('; ');
}

// ─── Taxas ──────────────────────────────────────────────────────────────────
/**
 * Taxas vigentes (rede) → matriz da tela: Débito + Crédito 1x..21x por
 * bandeira (null onde não há) e o PIX. Só a REDE (unit_id nulo); taxa por
 * unidade aparece à parte (excecoes).
 */
export function matrizTaxas(taxas = []) {
  const rede = (taxas || []).filter(t => t && !t.unit_id);
  const excecoes = (taxas || []).filter(t => t && t.unit_id);
  const pct = (t) => (t ? Number(t.pct) : null);
  const achar = (produto, bandeira, parcelas) => rede.find(t => t.produto === produto && t.bandeira === bandeira && Number(t.parcelas) === parcelas) || null;
  const linhas = [];
  const linhaDe = (produto, parcelas, rotulo) => {
    const vm = achar(produto, 'visa_master', parcelas);
    const elo = achar(produto, 'elo', parcelas);
    const amex = achar(produto, 'amex', parcelas);
    const outras = achar(produto, 'outras', parcelas);
    if (!vm && !elo && !amex && !outras) return;
    linhas.push({
      chave: `${produto}-${parcelas}`, rotulo, produto, parcelas,
      visa_master: pct(vm), elo: pct(elo), amex: pct(amex), outras: pct(outras),
      a_validar: [vm, elo, amex, outras].some(t => t && t.status === 'a_validar'),
    });
  };
  linhaDe('debito', 1, 'Débito');
  const maxParc = Math.max(1, ...rede.filter(t => t.produto === 'credito').map(t => Number(t.parcelas) || 1));
  for (let n = 1; n <= Math.max(21, maxParc); n++) linhaDe('credito', n, `Crédito ${n}x`);
  const pix = rede.find(t => t.produto === 'pix') || null;
  const vigencias = [...new Set(rede.map(t => t.vigencia_de).filter(Boolean))].sort();
  const origens = [...new Set(rede.map(t => t.origem).filter(Boolean))];
  return {
    linhas, pix: pix ? Number(pix.pct) : null, pix_status: pix?.status || null,
    vigencia_de: vigencias[vigencias.length - 1] || null, origens,
    temOutras: linhas.some(l => l.outras !== null), excecoes,
  };
}

/** Taxas vigentes num dia (rede e unidade), a mais nova vence. */
export function taxasVigentes(taxas = [], dia) {
  const ok = (taxas || []).filter(t => t && t.vigencia_de && t.vigencia_de <= dia && (!t.vigencia_ate || t.vigencia_ate >= dia));
  const melhor = new Map();
  for (const t of ok) {
    const k = [t.adquirente || 'stone', t.unit_id || '', t.produto, t.bandeira, t.parcelas].join('|');
    const atual = melhor.get(k);
    if (!atual || t.vigencia_de > atual.vigencia_de) melhor.set(k, t);
  }
  return [...melhor.values()];
}

/** Regra de prazo do servidor → frases para a tela. */
export function textoPrazo(prazo) {
  if (!prazo) return [];
  // status por parâmetro (recebivel_*) quando o servidor manda; senão o geral
  const porChave = prazo.status_por_chave && typeof prazo.status_por_chave === 'object' ? prazo.status_por_chave : null;
  const st = porChave || prazo.status;
  const CHAVE = { cartao_dias_uteis: 'recebivel_cartao_dias_uteis', pix: 'recebivel_pix_prazo_dias', feriado_local: 'recebivel_feriado_local', cartao: 'recebivel_cartao_prazo_dias' };
  const aValidar = (k) => (st && typeof st === 'object'
    ? (st[CHAVE[k]] ?? st[k]) === 'a_validar' || (st[CHAVE[k]] ?? st[k]) === 'ausente'
    : st === 'a_validar');
  const nCartao = Number(prazo.cartao_prazo_dias ?? 1);
  const uteis = Number(prazo.cartao_dias_uteis ?? 1) === 1;
  const nPix = Number(prazo.pix_prazo_dias ?? 0);
  const local = Number(prazo.feriado_local ?? 0) === 1;
  const frases = [];
  frases.push({
    texto: `Cartão (débito e crédito, à vista ou parcelado): cai em D+${nCartao} ${uteis ? 'dia útil' : 'corrido (todos os dias)'}.`,
    aValidar: aValidar('cartao_dias_uteis') || aValidar('cartao'),
    nota: 'Os prints da Stone divergem (útil × todos os dias): o 1º extrato confirma.',
  });
  frases.push({ texto: nPix === 0 ? 'PIX: cai na hora (D+0).' : `PIX: cai em D+${nPix}.`, aValidar: aValidar('pix') });
  frases.push({
    texto: local
      ? 'Feriados de SP (estadual 09/07 e municipal 25/01) também atrasam o recebível.'
      : 'Só feriados nacionais, Carnaval e Corpus Christi atrasam o recebível (09/07 e 25/01 de SP não).',
    aValidar: aValidar('feriado_local'),
  });
  return frases;
}

export const TIPO_FERIADO_TXT = { nacional: 'nacional', bancario: 'bancário', estadual_sp: 'estadual (SP)', municipal_sp: 'municipal (SP)' };

// ─── Erros ──────────────────────────────────────────────────────────────────
export const ehMigracaoPendente = (err) => err?.code === 'migracao_pendente';
/** Mensagem de erro para a tela (os códigos mais comuns em português simples). */
export function mensagemErro(err) {
  if (!err) return '';
  if (err.code === 'migracao_pendente') return 'A estrutura da Auditoria ainda não foi instalada no banco (migration financeiro-006).';
  if (err.code === 'sem_acesso' || err.status === 403) return 'Este login não é operador financeiro.';
  return err.message || String(err);
}

export default {
  SEVERIDADE, STATUS, rotuloTipo, textoMovimento, diffPagamentos, filtrosParaQuery, estadoDiaTexto, mascararConta,
  periodoPadraoRecebimentos,
};

// ─── Sem ruído, etapa C (22/10/2026): o extrato que já reconhece ────────────
// Textos das marcas novas do servidor (api/_lib/fin-extrato-identificado.js e
// fin-extrato-gabarito.js). Sem travessão e sem jargão. Só leitura: quem decide
// continua sendo a pessoa, pelos botões de sempre.
const ddmmTxt = (d) => { const m = /^\d{4}-(\d{2})-(\d{2})/.exec(String(d || '')); return m ? `${m[2]}/${m[1]}` : ''; };
// "Abrir o aviso" do crédito com diferença: um evento da janela (a Rotina e a Auditoria abrem o
// detalhe por cima, como a lista de avisos). Sem quem escute, nada acontece.
export const EVENTO_ABRIR_ALERTA = 'fk:fin-abrir-alerta';
export function pedirAbrirAlerta(id) {
  const n = Number(id);
  if (!Number.isFinite(n)) return;
  try { window.dispatchEvent(new CustomEvent(EVENTO_ABRIR_ALERTA, { detail: { id: n } })); } catch { /* sem janela (teste) */ }
}
/** identificado do crédito → { texto, tom: 'ok' | 'aviso' | 'neutro', alerta_id } | null */
export function textoIdentificado(x) {
  if (!x || typeof x !== 'object') return null;
  switch (x.estado) {
    case 'pix_comanda': return { texto: `Identificado: PIX da comanda nº ${x.numero ?? '?'} de ${ddmmTxt(x.dia_venda)}`, tom: 'ok' };
    case 'lote_fechado': return { texto: `Identificado: lote do cartão do depósito de ${ddmmTxt(x.dia_previsto)} (o dia bateu com as vendas)`, tom: 'ok' };
    case 'lote_diferenca': return {
      texto: `Lote do cartão do depósito de ${ddmmTxt(x.dia_previsto)}: o dia tem diferença${x.alerta_id ? `, veja o aviso nº ${x.alerta_id}` : ', veja o Fechamento do dia'}`,
      tom: 'aviso', alerta_id: x.alerta_id || null,
    };
    case 'aguardando': return { texto: `Aguardando a conferência do depósito de ${ddmmTxt(x.dia_previsto)} (ainda no prazo)`, tom: 'neutro' };
    case 'nao_e_venda': return { texto: 'Reembolso da própria Stone: não é venda', tom: 'ok' };
    case 'sem_conferencia': return { texto: 'Sem conferência agora: confira em Recebimentos', tom: 'neutro' };
    default: return null;
  }
}
/** devolução de venda (GABARITO 14) → texto */
export function textoDevolucaoVenda(d) {
  if (!d || typeof d !== 'object' || !d.par_linha_id) return '';
  const quem = d.papel === 'debito' ? `o PIX recebido da linha #${d.par_linha_id}` : `devolvido na linha #${d.par_linha_id}`;
  return d.papel === 'debito'
    ? `Parece a devolução de ${quem} ao mesmo pagador: não é despesa nem venda.`
    : `PIX recebido e ${quem} ao mesmo pagador: não é venda.`;
}
/** rateio feito à mão (GABARITO 15) → texto */
export function textoRateioNaMao(r) {
  if (!r || typeof r !== 'object' || !r.n) return '';
  const emp = Array.isArray(r.empate) && r.empate.length > 1 ? ` Há ${r.empate.length} débitos possíveis (linhas #${r.empate.join(', #')}).` : '';
  return `Parece rateio de ${fmtCentavos(r.total_cents)} em ${r.n} partes de ${fmtCentavos(r.parte_cents)} (débito da linha #${r.debito_linha_id}): crie o rateio em vez de lançar como despesa ou transferência entre empresas.${emp}`;
}
/** comissão com valor diferente do Avec (GABARITO 1) → texto */
export function textoComissaoDiferenca(c) {
  if (!c || typeof c !== 'object' || c.valor_0123_cents == null) return '';
  const d = Number(c.diferenca_cents) || 0;
  return `Comissão de ${ddmmTxt(c.quinzena?.de)} a ${ddmmTxt(c.quinzena?.ate)} no Avec: ${fmtCentavos(c.valor_0123_cents)} a pagar; este PIX tem ${fmtCentavos(Math.abs(d))} a ${d > 0 ? 'mais' : 'menos'}. Confira antes de lançar.`;
}
/** revisão do dono (GABARITO 3) → texto */
export function textoRevisaoExtrato(r) {
  if (!r || typeof r !== 'object') return '';
  if (r.motivo === 'transferencia_propria_como_despesa') return 'Para o dono revisar: transferência para a própria empresa lançada como despesa. Diga o que a outra conta pagou (ex.: a fatura do cartão).';
  return '';
}
